/**
 * useHolidayTheme — drives the storefront's holiday-theme skin.
 *
 * Three modes, picked from the URL in order of precedence:
 *   1. ``?theme_cycle=halloween,black_friday,christmas&cycle_ms=5000``
 *        Auto-rotate through the listed themes every ``cycle_ms`` ms.
 *        Pre-fetches each theme via the admin-only preview endpoint
 *        so it works on a logged-in admin session. Perfect for capturing
 *        an investor-demo GIF without manual clicks.
 *
 *   2. ``?preview_theme=halloween``
 *        Single-theme preview. Doesn't touch global settings.
 *
 *   3. (no params) → resolves the active theme from
 *        ``/api/plaza/themes/active`` (calendar-driven).
 *
 * Returns: `{ theme, loading, dismissed, dismiss, isPreview, cycleInfo }`
 *   where `cycleInfo = { index, total, ms } | null`.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../components/plaza/plazaLib";

const DISMISS_KEY = "hpz-holiday-theme-dismissed";
const MIN_CYCLE_MS = 1500;
const MAX_CYCLE_MS = 60000;

function applyPaletteStyle(styleNode, theme) {
  if (!theme?.palette) return;
  const p = theme.palette;
  styleNode.textContent = `
    .hpz-shop[data-holiday-theme="${theme.key}"] {
      --hpz-accent: ${p.accent};
      --hpz-banner-bg: ${p.banner_bg};
      --hpz-banner-fg: ${p.banner_fg};
    }
  `;
  styleNode.setAttribute("data-holiday-theme", theme.key);
  document.querySelector(".hpz-shop")?.setAttribute("data-holiday-theme", theme.key);
}

export function useHolidayTheme({ vendorId = null, region = "QLD" } = {}) {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [cycleInfo, setCycleInfo] = useState(null);
  const styleNodeRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const params = new URLSearchParams(window.location.search);
    const cycleRaw = params.get("theme_cycle");
    const previewKey = params.get("preview_theme");

    // Ensure a single <style> node exists for the lifetime of the hook
    const style = styleNodeRef.current || document.createElement("style");
    if (!styleNodeRef.current) {
      document.head.appendChild(style);
      styleNodeRef.current = style;
    }

    // ─── Mode 1: theme cycle ─────────────────────────────────────
    if (cycleRaw) {
      const keys = cycleRaw.split(",").map(k => k.trim()).filter(Boolean);
      const cycleMs = Math.min(
        MAX_CYCLE_MS,
        Math.max(MIN_CYCLE_MS, parseInt(params.get("cycle_ms"), 10) || 5000),
      );
      let idx = 0;
      let timer = null;
      const themes = {};

      // Pre-fetch all themes in parallel
      Promise.all(keys.map(k =>
        api.get(`/api/plaza/themes/preview/${encodeURIComponent(k)}`)
           .then(r => { themes[k] = r.data?.theme || null; })
           .catch(() => { themes[k] = null; }),
      )).then(() => {
        if (!mounted) return;
        const valid = keys.filter(k => themes[k]);
        if (!valid.length) {
          // No themes fetched (probably not admin). Bail to defaults.
          setLoading(false);
          return;
        }
        setIsPreview(true);
        setLoading(false);
        const tick = () => {
          if (!mounted) return;
          const key = valid[idx % valid.length];
          const t = themes[key];
          if (t) {
            setTheme(t);
            applyPaletteStyle(style, t);
            setCycleInfo({ index: (idx % valid.length) + 1,
                            total: valid.length,
                            ms: cycleMs,
                            key: t.key,
                            name: t.name });
          }
          idx += 1;
        };
        tick();                                     // first paint
        timer = setInterval(tick, cycleMs);
      });

      return () => {
        mounted = false;
        if (timer) clearInterval(timer);
        if (styleNodeRef.current) {
          styleNodeRef.current.remove();
          styleNodeRef.current = null;
        }
        document.querySelector(".hpz-shop")?.removeAttribute("data-holiday-theme");
      };
    }

    // ─── Mode 2 / Mode 3 ─────────────────────────────────────────
    const url = previewKey
      ? `/api/plaza/themes/preview/${encodeURIComponent(previewKey)}`
      : `/api/plaza/themes/active`;
    api.get(url, previewKey ? {} : {
      params: { ...(vendorId ? { vendor_id: vendorId } : {}), region },
    }).then(r => {
      if (!mounted) return;
      const t = r.data?.theme || null;
      setTheme(t);
      setIsPreview(!!previewKey && !!t);
      setLoading(false);
      if (t?.palette) applyPaletteStyle(style, t);
      if (!previewKey) {
        const dismissedKey = localStorage.getItem(DISMISS_KEY);
        if (t && dismissedKey === t.key) setDismissed(true);
      }
    }).catch(() => { if (mounted) setLoading(false); });

    return () => {
      mounted = false;
      if (styleNodeRef.current) {
        styleNodeRef.current.remove();
        styleNodeRef.current = null;
      }
      document.querySelector(".hpz-shop")?.removeAttribute("data-holiday-theme");
    };
  }, [vendorId, region]);

  const dismiss = useCallback(() => {
    if (theme?.key) localStorage.setItem(DISMISS_KEY, theme.key);
    setDismissed(true);
  }, [theme]);

  return { theme, loading, dismissed, dismiss, isPreview, cycleInfo };
}
